import { useMemo } from "react";
import React from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import ChartCard from "../Chart/ChartCard";
import { useDashboard } from "../../layouts/Dashboard/useDashboard";
import { useBackend } from "../../layouts/Wrapper";
import { TextWidgetModal } from "../TextWidgetModal";
import { ImageWidgetModal } from "../ImageWidgetModal";
import { EditWidgetModal } from "../EditWidgetModal";
import { Content } from "@radix-ui/react-tabs";
export const DashboardGrid = ({ className }) => {
    var _a;
    const { dashboard, widgets: allWidgets, setWidgets } = useDashboard();
    const { backend, adminMode } = useBackend();
    const ResponsiveGridLayout = useMemo(() => WidthProvider(Responsive), []);
    const editable = useMemo(() => {
        var _a;
        return adminMode || ((_a = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _a === void 0 ? void 0 : _a.can_edit_widget_layout);
    }, [adminMode, dashboard]);
    const grid_spacing = useMemo(() => {
        var _a, _b;
        return ((_a = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _a === void 0 ? void 0 : _a.grid_spacing) === undefined
            ? 10
            : (_b = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _b === void 0 ? void 0 : _b.grid_spacing;
    }, [dashboard]);
    const grid_height = useMemo(() => {
        var _a, _b;
        return ((_a = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _a === void 0 ? void 0 : _a.grid_height) === undefined
            ? 10
            : (_b = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _b === void 0 ? void 0 : _b.grid_height;
    }, [dashboard]);
    if (!dashboard)
        return (React.createElement("div", { className: "onvo-dashboard-grid-wrapper onvo-overflow-y-auto onvo-flex-grow onvo-pt-2 onvo-font-override onvo-background-color onvo-relative onvo-w-full " + (className || "") }));
    if (allWidgets.length === 0 &&
        (((_a = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _a === void 0 ? void 0 : _a.can_create_widgets) || adminMode)) {
        return (React.createElement("div", { className: "onvo-dashboard-grid-wrapper onvo-overflow-y-auto onvo-flex-grow onvo-pt-2 onvo-font-override onvo-background-color onvo-relative onvo-w-full " + (className || "") },
            React.createElement(TextWidgetModal, null),
            React.createElement(ImageWidgetModal, null),
            React.createElement(EditWidgetModal, null)));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(TextWidgetModal, null),
        React.createElement(ImageWidgetModal, null),
        React.createElement(EditWidgetModal, null),
        React.createElement("div", { className: "onvo-dashboard-grid-wrapper onvo-overflow-y-auto onvo-pt-6 onvo-scrollbar-thin onvo-flex-grow onvo-font-override onvo-background-color onvo-w-full " + (className || "") }, ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.tabs) || []).filter(a => !a.hidden).map((currentTab) => {
            const filteredWidgets = allWidgets.filter((i) => {
                return i.tab === currentTab.id;
            });
            const layouts = filteredWidgets.length === 0
                ? { lg: [], sm: [] }
                : {
                    lg: filteredWidgets.map((i) => {
                        let layouts = i.layouts;
                        return {
                            i: i.id,
                            x: layouts.lg.x,
                            y: layouts.lg.y,
                            w: layouts.lg.w,
                            h: layouts.lg.h,
                            minH: 4,
                        };
                    }),
                    sm: filteredWidgets.map((i) => {
                        var _a, _b, _c, _d;
                        let layouts = i.layouts;
                        return {
                            i: i.id,
                            x: ((_a = layouts.sm) === null || _a === void 0 ? void 0 : _a.x) || layouts.lg.x,
                            y: ((_b = layouts.sm) === null || _b === void 0 ? void 0 : _b.y) || layouts.lg.y,
                            w: ((_c = layouts.sm) === null || _c === void 0 ? void 0 : _c.w) || layouts.lg.w,
                            h: ((_d = layouts.sm) === null || _d === void 0 ? void 0 : _d.h) || layouts.lg.h,
                            minH: 4,
                        };
                    }),
                };
            const widgets = (filteredWidgets || []).map((i) => (React.createElement("div", { key: i.id },
                React.createElement(ChartCard, { widget: i }))));
            return (React.createElement(Content, { key: currentTab.id, value: currentTab.id.toString() },
                React.createElement(ResponsiveGridLayout, { resizeHandles: ["se", "sw"], draggableHandle: ".onvo-chart-card-drag-handle", className: "onvo-dashboard-grid-layout onvo-layout", rowHeight: grid_height, compactType: "vertical", margin: [grid_spacing, grid_spacing], breakpoints: { lg: 720, sm: 480 }, cols: { lg: 12, sm: 4 }, isDraggable: editable, isResizable: editable, layouts: layouts, containerPadding: [grid_spacing * 2, grid_spacing], onLayoutChange: (layout, allLayouts) => {
                        if (!dashboard || !editable)
                            return;
                        let newWidgets = allWidgets.filter(b => b.tab === currentTab.id).map((i) => {
                            let lgLayout = Object.assign({}, allLayouts.lg.find((j) => j.i === i.id));
                            let smLayout = Object.assign({}, allLayouts.sm.find((j) => j.i === i.id));
                            return Object.assign(Object.assign({}, i), { layouts: {
                                    lg: Object.assign(Object.assign({}, i.layouts.lg), { h: lgLayout.h, w: lgLayout.w, x: lgLayout.x, y: lgLayout.y }),
                                    sm: Object.assign(Object.assign({}, i.layouts.sm), { h: smLayout.h, w: smLayout.w, x: smLayout.x, y: smLayout.y }),
                                } });
                        });
                        // UPDATE ONLY THE WIDGETS THAT CHANGED
                        let updatedWidgets = newWidgets.filter((widget) => {
                            let oldWidget = allWidgets.find((i) => i.id === widget.id);
                            if (!oldWidget)
                                return true;
                            return (JSON.stringify(oldWidget.layouts) !==
                                JSON.stringify(widget.layouts));
                        });
                        if (dashboard && editable && updatedWidgets.length > 0) {
                            setWidgets([
                                ...allWidgets.filter((i) => i.tab !== currentTab.id),
                                ...newWidgets
                            ]);
                            updatedWidgets.forEach((i) => {
                                backend === null || backend === void 0 ? void 0 : backend.widgets.update(i.id, {
                                    layouts: i.layouts,
                                });
                            });
                        }
                    } }, widgets)));
        }))));
};
//# sourceMappingURL=DashboardGrid.js.map